import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import Modal from "../../../components/base/Modal";
import type { ActivityType } from "@torre/work-hours";
import {
  fetchActivityCatalog,
  fetchMe,
  fetchSummary,
  fetchTimeEntries,
  voidTimeEntry,
  type SummaryResponse,
  type TimeEntryRow,
  type WorkHoursMe,
} from "../services/workHoursService";
import { longDateLabel, localeOf, payPeriodPreset, presetRange, today, type RangePreset } from "../lib/heatmap";
import { buildDayGroups, buildWeekGroups, entryApprovalStatus, evidenceLinks, flagLines, formatMinutes } from "../lib/timeEntries";
import ManualEntryModal from "../components/ManualEntryModal";
import ActivityLink from "../components/ActivityLink";
import { kindLabel } from "../lib/kindLabel";
import { readFailureKey } from "../lib/requestFailure";
import { buildThemeClasses, inputClass } from "../theme";

// ---------------------------------------------------------------------------
// Entradas de tiempo por día (#783). Lo que se paga y factura son los minutos
// de las entradas; un día sin entradas muestra el estimado por commits con la
// marca «Estimado». Un admin puede anular una entrada con motivo (nunca se
// borra): deja de contar en tablero, nómina y reportes.
// ---------------------------------------------------------------------------

const VOID_REASON_MIN = 3;

/** Color del estado de aprobación (#1281): pendiente ámbar, rechazada roja, aprobada verde. */
const approvalTone = (status: string | null): string =>
  status === "pending" ? "text-amber-600" : status === "rejected" ? "text-red-500" : status === "approved" ? "text-emerald-600" : "";

/** #1061: vista por día o por semana; se recuerda en el navegador. */
type EntriesView = "day" | "week";
const VIEW_KEY = "workHours.timeEntries.view";
const readView = (): EntriesView => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === "week" ? "week" : "day";
  } catch {
    return "day";
  }
};

export default function TimeEntriesSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const locale = localeOf(i18n?.language);

  const [me, setMe] = useState<WorkHoursMe | null>(null);
  const [meReady, setMeReady] = useState(false);
  const [preset, setPreset] = useState<RangePreset>("7d");
  const [range, setRange] = useState(presetRange("7d"));
  const [userId, setUserId] = useState("");
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [entries, setEntries] = useState<TimeEntryRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Anulación: la entrada elegida, el motivo y el estado del envío.
  const [voidTarget, setVoidTarget] = useState<TimeEntryRow | null>(null);
  const [reason, setReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [voidErrorKey, setVoidErrorKey] = useState<string | null>(null);
  const [view, setView] = useState<EntriesView>(readView);
  // #1060: formulario de entrada manual.
  const [adding, setAdding] = useState(false);
  // #1306: catálogo de actividades, para nombrar el tipo de cada entrada. Si no
  // carga, la etiqueta cae al texto base de i18n (o a la clave).
  const [catalog, setCatalog] = useState<ActivityType[] | null>(null);

  const changeView = (next: EntriesView) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* sin almacenamiento: la vista vale solo en esta sesión */
    }
  };

  useEffect(() => {
    fetchMe()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setMeReady(true));
    fetchActivityCatalog()
      .then((res) => setCatalog(res.data))
      .catch(() => setCatalog([]));
  }, []);

  const isAdmin = Boolean(me?.isAdmin);

  /** Tooltip del estado de aprobación (#1281): quién decidió y cuándo, o que sigue esperando. */
  const approvalTitle = (e: TimeEntryRow): string => {
    const a = e.approval;
    if (!a) return "";
    if (a.status === "pending") return t("workHours.timeEntries.approval.pendingHint");
    return t("workHours.timeEntries.approval.decidedBy", { name: a.decidedByName || a.decidedBy || "—", date: a.decidedAt ? a.decidedAt.slice(0, 10) : "—" });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErrorKey(null);
    try {
      const scope = isAdmin ? (userId ? { userId } : { all: true }) : {};
      const [s, e] = await Promise.all([
        fetchSummary({ from: range.from, to: range.to, userId: userId || undefined }),
        fetchTimeEntries({ from: range.from, to: range.to, ...scope }),
      ]);
      setSummary(s);
      setEntries(e.rows);
      setTruncated(e.truncated);
    } catch (err) {
      setErrorKey(readFailureKey(err, { failed: "workHours.timeEntries.loadError", timeout: "workHours.timeEntries.timeout" }));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, userId, range.from, range.to]);

  useEffect(() => {
    if (meReady) void load();
  }, [meReady, load]);

  const names = useMemo(
    () => Object.fromEntries((summary?.users ?? []).map((u) => [u.userId, u.userName])),
    [summary?.users],
  );
  const groups = useMemo(() => buildDayGroups(summary?.summary.days, entries, names), [summary, entries, names]);
  const recordedMinutes = groups.filter((g) => !g.estimated).reduce((n, g) => n + g.minutes, 0);
  const estimatedGroups = groups.filter((g) => g.estimated);
  const estimatedHours = Math.round(estimatedGroups.reduce((n, g) => n + g.hours, 0) * 100) / 100;
  const teamView = isAdmin && !userId;
  const weeks = useMemo(() => buildWeekGroups(groups), [groups]);

  const closeVoid = () => {
    if (voiding) return;
    setVoidTarget(null);
    setReason("");
    setVoidErrorKey(null);
  };

  const confirmVoid = async () => {
    if (!voidTarget) return;
    if (reason.trim().length < VOID_REASON_MIN) {
      setVoidErrorKey("workHours.timeEntries.void.reasonTooShort");
      return;
    }
    setVoiding(true);
    setVoidErrorKey(null);
    try {
      await voidTimeEntry(voidTarget.id, reason.trim());
      setVoiding(false);
      setVoidTarget(null);
      setReason("");
      setNotice(t("workHours.timeEntries.void.done"));
      await load();
    } catch {
      setVoiding(false);
      setVoidErrorKey("workHours.timeEntries.void.error");
    }
  };

  const card = `rounded-2xl border p-4 ${tc.cardBg} ${tc.border}`;

  return (
    <div className="w-full max-w-7xl overflow-y-auto p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${tc.text}`}>{t("workHours.timeEntries.title")}</h1>
          <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.timeEntries.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(["today", "7d", "30d", "90d"] as const).map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`range-${p}`}
              onClick={() => {
                setPreset(p);
                setRange(presetRange(p));
              }}
              className={`rounded-lg px-3 py-2 text-xs font-medium ${
                preset === p ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary} border ${tc.border}`
              }`}
            >
              {t(`workHours.range.${p}`)}
            </button>
          ))}
          {(["qCurrent", "qPrevious"] as const).map((p) => {
            const period = payPeriodPreset(p);
            return (
              <button
                key={p}
                type="button"
                data-testid={`range-${p}`}
                title={t("workHours.range.payPeriodHint", { from: period.from, to: period.to, payDate: period.payDate })}
                onClick={() => {
                  setPreset(p);
                  setRange(presetRange(p));
                }}
                className={`rounded-lg px-3 py-2 text-xs font-medium ${
                  preset === p ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary} border ${tc.border}`
                }`}
              >
                {t(`workHours.range.${p}`)}
              </button>
            );
          })}
          <input
            type="date"
            aria-label={t("workHours.filters.from")}
            value={range.from}
            max={range.to}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, from: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          <input
            type="date"
            aria-label={t("workHours.filters.to")}
            value={range.to}
            min={range.from}
            max={today()}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, to: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          {isAdmin && (
            <select aria-label={t("workHours.filters.user")} value={userId} onChange={(e) => setUserId(e.target.value)} className={inputClass(isDarkMode)}>
              <option value="">{t("workHours.filters.team")}</option>
              {(summary?.users ?? []).map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.userName}
                </option>
              ))}
            </select>
          )}
          <div className={`flex overflow-hidden rounded-lg border ${tc.border}`} role="group" aria-label={t("workHours.timeEntries.view.label")}>
            {(["day", "week"] as const).map((v) => (
              <button
                key={v}
                type="button"
                data-testid={`view-${v}`}
                aria-pressed={view === v}
                onClick={() => changeView(v)}
                className={`px-3 py-2 text-xs font-medium ${view === v ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary}`}`}
              >
                {t(`workHours.timeEntries.view.${v}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="add-entry"
            onClick={() => setAdding(true)}
            className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white"
          >
            <i className="ri-add-line mr-1" />
            {t("workHours.timeEntries.add.action")}
          </button>
        </div>
      </div>

      <p className={`rounded-lg p-2 text-xs ${tc.textSecondary} border ${tc.border}`}>
        <i className="ri-information-line mr-1" />
        {t("workHours.rule")}
      </p>

      {notice && (
        <div className="flex items-start justify-between gap-2 rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-600" data-testid="time-entries-notice">
          <span>{notice}</span>
          <button type="button" aria-label="close-notice" onClick={() => setNotice(null)}>
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {errorKey && (
        <div
          data-testid="time-entries-error"
          className={`rounded-lg p-3 text-sm ${errorKey === "workHours.timeEntries.timeout" ? "bg-amber-500/10 text-amber-600" : "bg-red-500/10 text-red-500"}`}
        >
          <p>{t(errorKey)}</p>
          <button type="button" onClick={() => void load()} className="mt-2 rounded-md border border-current px-2 py-1 text-[11px] font-medium opacity-90">
            <i className="ri-refresh-line mr-1" />
            {t("workHours.retry")}
          </button>
        </div>
      )}
      {truncated && <p className="rounded-lg bg-amber-500/10 p-2 text-[11px] text-amber-500">{t("workHours.timeEntries.truncated")}</p>}
      {loading && <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.loading")}</p>}

      {!loading && !errorKey && (
        <>
          <p className={`text-sm font-medium ${tc.text}`} data-testid="time-entries-totals">
            {t("workHours.timeEntries.totals", {
              recorded: formatMinutes(recordedMinutes),
              estimated: estimatedHours,
              days: estimatedGroups.length,
            })}
          </p>
          {groups.length === 0 && <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.timeEntries.empty")}</p>}
          {view === "week" && (
            <div className="space-y-3" data-testid="week-view">
              {weeks.map((w) => (
                <div key={w.weekStart} className={card} data-testid={`week-${w.weekStart}`}>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className={`text-sm font-semibold ${tc.text}`}>
                      {t("workHours.timeEntries.view.weekOf", { from: longDateLabel(w.weekStart, locale), to: longDateLabel(w.weekEnd, locale) })}
                    </p>
                    <span className={`text-sm font-bold ${tc.text}`} data-testid={`week-total-${w.weekStart}`}>
                      {formatMinutes(w.recordedMinutes)} h
                      {w.estimatedHours > 0 && (
                        <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-600">
                          +{w.estimatedHours} h {t("workHours.timeEntries.estimated")}
                        </span>
                      )}
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {w.days.map((d) => (
                        <tr key={d.key} className={`border-t ${tc.border} ${tc.text}`}>
                          <td className="py-1.5 pr-2">
                            {longDateLabel(d.date, locale)}
                            {teamView && <span className={`ml-2 ${tc.textSecondary}`}>· {d.userName ?? d.userId}</span>}
                          </td>
                          <td className={`py-1.5 pr-2 text-xs ${tc.textSecondary}`}>
                            {d.estimated ? t("workHours.timeEntries.estimated") : t("workHours.timeEntries.view.entries", { count: d.entries.length })}
                          </td>
                          <td className="py-1.5 text-right font-semibold whitespace-nowrap">
                            {d.estimated ? `${d.hours} h` : `${formatMinutes(d.minutes)} h`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
          <div className={`space-y-3 ${view === "week" ? "hidden" : ""}`}>
            {groups.map((g) => (
              <div key={g.key} className={card} data-testid={`day-${g.date}-${g.userId}`}>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className={`text-sm font-semibold ${tc.text}`}>
                    {longDateLabel(g.date, locale)}
                    {teamView && <span className={`ml-2 font-normal ${tc.textSecondary}`}>· {g.userName ?? g.userId}</span>}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${tc.text}`}>{formatMinutes(g.minutes)} h</span>
                    {/* #843: tiempo fuera de asignación — cuenta, pero se señala con su motivo. */}
                    {g.outOfAllocationMinutes > 0 && (
                      <span
                        className="rounded-full bg-orange-500/15 px-2 py-0.5 text-[11px] font-medium text-orange-600"
                        title={flagLines(g.flags)
                          .map((f) => `${f.message} — ${f.reason ? t("workHours.timeEntries.flagReason", { reason: f.reason }) : t("workHours.timeEntries.noJustification")}`)
                          .join("\n")}
                        data-testid="badge-out-of-allocation"
                      >
                        {t("workHours.timeEntries.outOfAllocation")} · {formatMinutes(g.outOfAllocationMinutes)} h
                      </span>
                    )}
                    {/* #1281: minutos que esperan al tech lead — fuera del total del día. */}
                    {g.pendingMinutes > 0 && (
                      <span
                        className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-600"
                        title={t("workHours.timeEntries.approval.pendingHint")}
                        data-testid="badge-pending"
                      >
                        <i className="ri-hourglass-line mr-1" />
                        {t("workHours.timeEntries.approval.pending")} · {formatMinutes(g.pendingMinutes)} h
                      </span>
                    )}
                    {g.estimated ? (
                      <span
                        className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-600"
                        title={t("workHours.timeEntries.estimatedHint")}
                        data-testid="badge-estimated"
                      >
                        {t("workHours.timeEntries.estimated")}
                      </span>
                    ) : (
                      g.minutes > 0 && (
                        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-600" data-testid="badge-registered">
                          {t("workHours.timeEntries.registered")}
                        </span>
                      )
                    )}
                  </div>
                </div>

                {g.estimated && (
                  <>
                    <p className={`text-xs ${tc.textSecondary}`}>
                      {t("workHours.timeEntries.estimatedBody", {
                        time: formatMinutes(g.minutes),
                        count: g.blocks.length,
                        ranges: g.blocks.map((b) => `${b.start}–${b.end}`).join(", ") || "—",
                        commits: g.commitCount,
                      })}
                    </p>
                    {g.outOfAllocationMinutes > 0 && (
                      <ul className="mt-1 space-y-0.5 text-[11px] text-orange-600" data-testid="estimated-flags">
                        <li className="font-medium">{t("workHours.timeEntries.outOfAllocationDay", { time: formatMinutes(g.outOfAllocationMinutes) })}</li>
                        {flagLines(g.flags).map((f, i) => (
                          <li key={`${f.message}-${i}`}>
                            {f.message} · <span className="italic">{f.reason ? t("workHours.timeEntries.flagReason", { reason: f.reason }) : t("workHours.timeEntries.noJustification")}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
                {g.entries.length > 0 && (
                  <div className={`overflow-x-auto ${g.estimated ? "mt-2" : ""}`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`text-left text-xs ${tc.textSecondary}`}>
                          <th className="py-1 pr-2">{t("workHours.timeEntries.col.project")}</th>
                          <th className="py-1 pr-2">{t("workHours.timeEntries.col.repo")}</th>
                          <th className="py-1 pr-2 text-right">{t("workHours.timeEntries.col.time")}</th>
                          <th className="py-1 pr-2">{t("workHours.timeEntries.col.kind")}</th>
                          <th className="py-1 pr-2">{t("workHours.timeEntries.col.activity")}</th>
                          <th className="py-1 pr-2">{t("workHours.timeEntries.col.note")}</th>
                          <th className="py-1 pr-2">{t("workHours.timeEntries.col.evidence")}</th>
                          {isAdmin && <th className="py-1 text-right">{t("workHours.timeEntries.col.actions")}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {g.entries.map((e) => {
                          const links = evidenceLinks(e.evidence);
                          return (
                            <tr key={e.id} className={`border-t ${tc.border} ${tc.text}`} data-testid={`entry-${e.id}`}>
                              <td className="py-1.5 pr-2">
                                <span className="whitespace-nowrap">{e.projectLabel || e.projectId}</span>
                                {flagLines(e.flags).map((f, i) => (
                                  <span key={`${f.message}-${i}`} className="mt-0.5 block text-[11px] text-orange-600" data-testid={`entry-flag-${e.id}`}>
                                    <span
                                      className="mr-1 rounded-full bg-orange-500/15 px-1.5 py-0.5 font-medium"
                                      title={`${f.message} — ${f.reason ? t("workHours.timeEntries.flagReason", { reason: f.reason }) : t("workHours.timeEntries.noJustification")}`}
                                    >
                                      {t("workHours.timeEntries.outOfAllocation")}
                                    </span>
                                    {f.message}
                                    <span className="block italic">
                                      {f.reason ? t("workHours.timeEntries.flagReason", { reason: f.reason }) : t("workHours.timeEntries.noJustification")}
                                    </span>
                                  </span>
                                ))}
                              </td>
                              <td className="py-1.5 pr-2 text-[11px] whitespace-nowrap" data-testid={`entry-repo-${e.id}`}>
                                {e.repo ? (
                                  <span className="font-mono">{e.repo}</span>
                                ) : (
                                  <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-amber-600">{t("workHours.timeEntries.noRepo")}</span>
                                )}
                              </td>
                              <td className="py-1.5 pr-2 text-right font-semibold whitespace-nowrap">
                                {formatMinutes(e.minutes)}
                                {e.start && e.end && <span className={`block text-[11px] font-normal ${tc.textSecondary}`}>{e.start}–{e.end}</span>}
                                {/* #1281: estado ante el tech lead; sin aprobación no se pinta nada. */}
                                {entryApprovalStatus(e) && (
                                  <span
                                    className={`mt-0.5 block text-[11px] font-medium ${approvalTone(entryApprovalStatus(e))}`}
                                    title={approvalTitle(e)}
                                    data-testid={`entry-approval-${e.id}`}
                                    data-status={entryApprovalStatus(e)}
                                  >
                                    {t(`workHours.timeEntries.approval.${entryApprovalStatus(e)}`)}
                                    {entryApprovalStatus(e) === "rejected" && e.approval?.reason && <span className="block font-normal italic">{t("workHours.timeEntries.approval.reason", { reason: e.approval.reason })}</span>}
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 pr-2 whitespace-nowrap">{kindLabel(e.kind, catalog, t, i18n?.language)}</td>
                              <td className="max-w-[16rem] py-1.5 pr-2">
                                {/* #1303: ticket al que está ligada la entrada; las anteriores dicen «Sin ticket». */}
                                <ActivityLink activityId={e.activityId} activityKey={e.activityKey} activityTitle={e.activityTitle} secondaryClass={tc.textSecondary} testId={`entry-activity-${e.id}`} />
                              </td>
                              <td className="py-1.5 pr-2">
                                {e.note}
                                {/* #1404: con quiénes fue la reunión y, si es la copia de un asistente, quién la registró. */}
                                {e.meeting && (
                                  <span className={`mt-0.5 block text-[11px] ${tc.textSecondary}`} data-testid={`entry-meeting-${e.id}`}>
                                    <i className="ri-group-line mr-1" />
                                    {t("workHours.timeEntries.meeting.with", {
                                      names: [...(e.meeting.attendees ?? []).map((a) => a.name || a.teamMemberId), e.meeting.others].filter(Boolean).join(", ") || "—",
                                    })}
                                    {e.meeting.mirrorOf && <span className="block italic">{t("workHours.timeEntries.meeting.registeredBy", { name: e.meeting.organizerName || "—" })}</span>}
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 pr-2 text-[11px]">
                                {links.length === 0
                                  ? t("workHours.timeEntries.noEvidence")
                                  : links.map((l, i) => (
                                      <span key={`${l.kind}-${l.label}-${i}`}>
                                        {i > 0 && ", "}
                                        {l.url ? (
                                          <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-violet-500 hover:underline">
                                            {l.label}
                                          </a>
                                        ) : (
                                          <span className="font-mono">{l.label}</span>
                                        )}
                                      </span>
                                    ))}
                              </td>
                              {isAdmin && (
                                <td className="py-1.5 text-right">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setVoidTarget(e);
                                      setReason("");
                                      setVoidErrorKey(null);
                                    }}
                                    className="rounded-md border border-red-500/40 px-2 py-1 text-[11px] font-medium text-red-500 hover:bg-red-500/10"
                                  >
                                    <i className="ri-forbid-line mr-1" />
                                    {t("workHours.timeEntries.void.action")}
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {adding && (
        <ManualEntryModal
          isDarkMode={isDarkMode}
          onClose={() => setAdding(false)}
          onSaved={(result) => {
            setAdding(false);
            // #1281: si quedó esperando al tech lead, decirlo: aún no cuenta.
            // #1404: una reunión dice también a cuántos asistentes se les registró y a quién no.
            const mirrored = (result?.attendees ?? []).filter((a) => a.ok).length;
            const missed = (result?.attendees ?? []).filter((a) => !a.ok).map((a) => a.name || a.teamMemberId);
            setNotice(
              [
                t(result?.approval === "pending" ? "workHours.timeEntries.add.donePending" : "workHours.timeEntries.add.done"),
                mirrored ? t("workHours.timeEntries.add.doneAttendees", { count: mirrored }) : "",
                missed.length ? t("workHours.timeEntries.add.attendeesFailed", { names: missed.join(", ") }) : "",
              ]
                .filter(Boolean)
                .join(" "),
            );
            void load();
          }}
        />
      )}

      {voidTarget && (
        <Modal
          title={t("workHours.timeEntries.void.title")}
          themeClasses={tc}
          onClose={closeVoid}
          width="max-w-lg"
          content={
            <div className="space-y-3" data-testid="void-modal">
              <p className={`text-sm ${tc.text}`}>
                {t("workHours.timeEntries.void.body", {
                  time: formatMinutes(voidTarget.minutes),
                  project: voidTarget.projectLabel || voidTarget.projectId,
                  date: voidTarget.date,
                })}
              </p>
              <label className={`block text-xs font-medium ${tc.textSecondary}`}>
                {t("workHours.timeEntries.void.reasonLabel")}
                <textarea
                  aria-label={t("workHours.timeEntries.void.reasonLabel")}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("workHours.timeEntries.void.reasonPlaceholder")}
                  maxLength={500}
                  rows={3}
                  className={`mt-1 w-full ${inputClass(isDarkMode)}`}
                />
              </label>
              {voidErrorKey && (
                <p className="text-xs text-red-500" data-testid="void-error">
                  {t(voidErrorKey)}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeVoid} disabled={voiding} className={`rounded-lg border px-3 py-2 text-xs ${tc.border} ${tc.textSecondary}`}>
                  {t("workHours.cancel")}
                </button>
                <button
                  type="button"
                  onClick={() => void confirmVoid()}
                  disabled={voiding}
                  className="rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-60"
                >
                  {voiding ? t("workHours.timeEntries.void.voiding") : t("workHours.timeEntries.void.confirm")}
                </button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
